import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, Award, Sparkles } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get(`/modules?lang=${lang}`).then((r) => setModules(r.data)).catch(() => {});
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, [lang]);


  const completed = new Set(progress?.completed_lessons || []);
  const allLessons = modules.flatMap((m) => m.lessons.map((l) => ({ ...l, module_title: m.title })));
  const lastId = progress?.last_lesson;
  const nextLesson = allLessons.find((l) => !completed.has(l.lesson_id));
  const currentModule = modules.find((m) => m.lessons.some((l) => l.lesson_id === (lastId || nextLesson?.lesson_id)));
  const continueLesson = lastId ? allLessons.find((l) => l.lesson_id === lastId) : nextLesson;

  // Lesson of the day: stable per calendar day, prefers an uncompleted lesson,
  // and never duplicates the continue/next card.
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const notContinue = (l) => l.lesson_id !== continueLesson?.lesson_id;
  const uncompleted = allLessons.filter((l) => !completed.has(l.lesson_id) && notContinue(l));
  const pool = uncompleted.length ? uncompleted : allLessons.filter(notContinue);
  const daily = progress && pool.length ? pool[dayOfYear % pool.length] : null;

  return (
    <div className="min-h-screen bg-void">
      <Navbar />
      <main className="pt-28 pb-16 max-w-7xl mx-auto px-6 md:px-12" data-testid="dashboard">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">{t("dash.welcome")}</p>
          <h1 className="font-serif text-5xl md:text-6xl text-ink mb-12">{user?.name}</h1>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="md:col-span-2 bg-graphite border border-ink/5 p-8">
            <div className="flex justify-between items-baseline mb-4">
              <span className="text-ink-soft text-sm tracking-widest uppercase">{t("dash.progress")}</span>
              <span className="font-serif text-4xl text-gold">{progress?.percent ?? 0}%</span>
            </div>
            <div className="h-1 bg-ink/10 mb-3">
              <div className="h-full bg-gold transition-all duration-700" style={{ width: `${progress?.percent ?? 0}%` }} />
            </div>
            <p className="text-ink-muted text-sm">
              {progress?.completed_count ?? 0} {t("common.of")} {progress?.total ?? 0} {t("dash.lessons")} {t("dash.done")}
            </p>
          </div>

          <div className="bg-graphite border border-ink/5 p-8 flex flex-col justify-between">
            <span className="text-ink-soft text-sm tracking-widest uppercase mb-2">{t("dash.current")}</span>
            <span className="font-serif text-2xl text-ink leading-tight">{currentModule?.title || (progress?.percent === 100 ? t("lesson.completed") : "—")}</span>
          </div>
        </div>

        {daily && (
          <Link to={`/lesson/${daily.lesson_id}`} data-testid="dashboard-daily"
            className="group flex items-center justify-between bg-gradient-to-r from-graphite to-surface border border-ink/5 hover:border-gold/40 p-6 mb-8 transition-colors">
            <div className="flex items-center gap-4">
              <Sparkles className="text-gold shrink-0" strokeWidth={1.5} size={22} />
              <div>
                <span className="text-gold text-xs tracking-[0.3em] uppercase">{t("dash.daily")}</span>
                <div className="font-serif text-2xl text-ink mt-1 leading-snug">{daily.title}</div>
                <div className="text-ink-muted text-sm">{daily.module_title}</div>
              </div>
            </div>
            <ArrowRight className="text-gold group-hover:translate-x-1 transition-transform shrink-0" size={22} strokeWidth={1.5} />
          </Link>
        )}

        {progress?.percent === 100 && (
          <Link to="/certificate" data-testid="dashboard-certificate"
            className="group flex items-center justify-between bg-graphite border border-gold/30 hover:border-gold p-6 mb-8 transition-colors">
            <div className="flex items-center gap-3">
              <Award className="text-gold" strokeWidth={1.5} size={24} />
              <span className="font-serif text-2xl text-ink">{t("dash.certificate")}</span>
            </div>
            <ArrowRight className="text-gold group-hover:translate-x-1 transition-transform" size={24} strokeWidth={1.5} />
          </Link>
        )}

        {continueLesson && (
          <Link to={`/lesson/${continueLesson.lesson_id}`} data-testid="dashboard-continue"
            className="group block bg-surface border border-ink/5 hover:border-gold/30 p-8 mb-16 transition-colors">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-gold text-xs tracking-[0.3em] uppercase">
                  {lastId ? t("dash.continue") : t("dash.next")}
                </span>
                <div className="font-serif text-3xl text-ink mt-2">{continueLesson.title}</div>
                <div className="text-ink-muted text-sm mt-1">{continueLesson.module_title}</div>
              </div>
              <ArrowRight className="text-gold group-hover:translate-x-1 transition-transform" size={28} strokeWidth={1.5} />
            </div>
          </Link>
        )}

        <h2 className="font-serif text-3xl text-ink mb-8">{t("dash.modules")}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dashboard-modules">
          {modules.map((m, i) => {
            const done = m.lessons.filter((l) => completed.has(l.lesson_id)).length;
            const pct = m.lessons.length ? Math.round((done / m.lessons.length) * 100) : 0;
            return (
              <Link key={m.module_id} to={`/course#${m.module_id}`}
                className="group bg-graphite border border-ink/5 hover:border-gold/30 p-6 transition-colors"
                data-testid={`dashboard-module-${m.module_id}`}>
                <div className="flex justify-between items-start mb-4">
                  <span className="font-mono text-xs text-ink-muted">{String(i + 1).padStart(2, "0")}</span>
                  {pct === 100 && <span className="w-2 h-2 rounded-full bg-gold" title={t("lesson.completed")} />}
                </div>
                <h3 className="font-serif text-xl text-ink mb-2 leading-snug group-hover:text-gold transition-colors">{m.title}</h3>
                <p className="text-ink-muted text-sm mb-4 line-clamp-2">{m.description}</p>
                <div className="h-px bg-ink/10 mb-2"><div className="h-px bg-gold transition-all" style={{ width: `${pct}%` }} /></div>
                <span className="text-xs text-ink-muted">{done}/{m.lessons.length} {t("dash.lessons")}</span>
              </Link>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
